'use client';

import * as PopoverPrimitive from '@radix-ui/react-popover';
import { IconChevronLeft, IconChevronRight, IconX } from '@tabler/icons-react';
import { format } from 'date-fns/format';
import { useEffect, useState } from 'react';

import { BaseUIComponentProps } from './base';
import { Button, IconButton } from './button';
import { DropdownTriggerButton } from './dropdown-trigger-button';
import { InputGroup, InputGroupProps } from './form/inputgroup';
import { Paragraph } from './paragraph';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

type MonthPickerProps = {
    /** Selected month, normalised to the first day of the month. */
    value?: Date | null;
    onChange?: (date: Date | null) => void;

    placeholder?: string;
    formatStr?: string;

    disabled?: boolean;
    noClear?: boolean;
    noInputGroup?: boolean;

    minDate?: Date;
    maxDate?: Date;

    wrapperClassName?: string;
} & Pick<InputGroupProps, 'label' | 'hasError' | 'errorMessage'> &
    BaseUIComponentProps;

const monthOrdinal = (year: number, monthIndex: number) => year * 12 + monthIndex;
const toMonthOrdinal = (date: Date) => monthOrdinal(date.getUTCFullYear(), date.getUTCMonth());

export function MonthPicker({
    value,
    onChange,
    mode,
    className,
    wrapperClassName,
    label,
    hasError,
    errorMessage,
    noClear = false,
    noInputGroup = false,
    placeholder = 'Select a month',
    formatStr = 'MMMM yyyy',
    disabled,
    minDate,
    maxDate,
}: MonthPickerProps) {
    const [internalValue, setInternalValue] = useState<Date | null>(value ?? null);
    const [isOpen, setIsOpen] = useState(false);
    const [viewYear, setViewYear] = useState<number>((value ?? new Date()).getUTCFullYear());

    const minOrdinal = minDate ? toMonthOrdinal(minDate) : Number.NEGATIVE_INFINITY;
    const maxOrdinal = maxDate ? toMonthOrdinal(maxDate) : Number.POSITIVE_INFINITY;
    const isMonthSelectable = (year: number, monthIndex: number) => {
        const ordinal = monthOrdinal(year, monthIndex);
        return ordinal >= minOrdinal && ordinal <= maxOrdinal;
    };
    // A year is worth navigating to when at least one of its months is selectable
    const canShowPreviousYear = monthOrdinal(viewYear - 1, 11) >= minOrdinal;
    const canShowNextYear = monthOrdinal(viewYear + 1, 0) <= maxOrdinal;

    useEffect(() => {
        if (value !== undefined) {
            setInternalValue(value ?? null);
        }
    }, [value]);

    const handleOpenChange = (open: boolean) => {
        if (disabled) {
            return;
        }
        if (open) {
            setViewYear((internalValue ?? new Date()).getUTCFullYear());
        }
        setIsOpen(open);
    };

    const handleSelect = (monthIndex: number) => {
        // Store the month as UTC midnight so it round-trips through a UTC server
        // (and the @db.Date column) without shifting a day across timezones.
        const next = new Date(Date.UTC(viewYear, monthIndex, 1));
        setInternalValue(next);
        onChange?.(next);
        setIsOpen(false);
    };

    const handleClear = () => {
        setInternalValue(null);
        onChange?.(null);
    };

    const hasValue = internalValue !== null;
    const selectedMonth =
        internalValue && internalValue.getUTCFullYear() === viewYear ? internalValue.getUTCMonth() : null;
    // Values are UTC midnight; format from the UTC calendar components so the label
    // shows the selected month in every timezone (date-fns `format` uses local time).
    const displayValue = internalValue
        ? new Date(internalValue.getUTCFullYear(), internalValue.getUTCMonth(), 1)
        : null;

    const node = (
        <PopoverPrimitive.Root open={isOpen} onOpenChange={handleOpenChange}>
            <PopoverPrimitive.Trigger asChild className="focus:outline-none">
                <DropdownTriggerButton
                    isOpen={isOpen}
                    mode={mode}
                    className={className}
                    disabled={disabled}
                    hasError={hasError}
                >
                    {hasValue ? format(displayValue as Date, formatStr) : placeholder}

                    {noClear || !hasValue ? null : (
                        <div className="absolute top-0 bottom-0 right-6 mr-2 flex items-center justify-center">
                            <IconX
                                size={16}
                                className="text-token-text-subtitle-defaultNew cursor-pointer group-focus:text-neutral-50 group-hover:text-neutral-50 focus:text-white focus:outline-none"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    handleClear();
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        handleClear();
                                    }
                                }}
                                tabIndex={0}
                            />
                        </div>
                    )}
                </DropdownTriggerButton>
            </PopoverPrimitive.Trigger>
            <PopoverPrimitive.Content className="z-50">
                <div className="mt-2 w-[var(--radix-popover-trigger-width)] min-w-[260px] rounded-lg border border-token-stroke-input-focused bg-token-bg-dp01 p-4">
                    <div className="flex items-center justify-between pb-3">
                        <IconButton
                            icon={IconChevronLeft}
                            mode={mode}
                            aria-label="Previous year"
                            disabled={!canShowPreviousYear}
                            onClick={() => setViewYear((year) => year - 1)}
                        />
                        <Paragraph mode={mode} fontWeight="medium">
                            {viewYear}
                        </Paragraph>
                        <IconButton
                            icon={IconChevronRight}
                            mode={mode}
                            aria-label="Next year"
                            disabled={!canShowNextYear}
                            onClick={() => setViewYear((year) => year + 1)}
                        />
                    </div>
                    <div className="grid grid-cols-3 gap-1.5">
                        {MONTH_LABELS.map((monthLabel, monthIndex) => (
                            <Button
                                key={monthLabel}
                                type="button"
                                mode={mode}
                                size="sm"
                                variant={selectedMonth === monthIndex ? 'primary' : 'tertiary'}
                                disabled={!isMonthSelectable(viewYear, monthIndex)}
                                onClick={() => handleSelect(monthIndex)}
                            >
                                {monthLabel}
                            </Button>
                        ))}
                    </div>
                </div>
            </PopoverPrimitive.Content>
        </PopoverPrimitive.Root>
    );

    if (noInputGroup) {
        return node;
    }

    return (
        <InputGroup
            label={label}
            hasError={hasError}
            errorMessage={errorMessage}
            mode={mode}
            className={wrapperClassName}
        >
            {node}
        </InputGroup>
    );
}
